import React, { useEffect, useRef, useState, useMemo } from "react";
import styled, { keyframes } from "styled-components";

/* ─── Animations ──────────────────────────────────────────────────────────── */
const grow = keyframes`from{transform:scaleX(0)}to{transform:scaleX(1)}`;

/* ─── Paths SVG simplifiés par code pays ─────────────────────────────────── */
const COUNTRY_PATHS = {
  FR:"M487 115 L492 112 L498 116 L502 120 L498 130 L492 134 L485 130 L483 122 Z",
  BE:"M489 108 L494 106 L500 109 L499 115 L493 116 L488 113 Z",
  CH:"M494 118 L501 116 L506 119 L505 124 L498 125 Z",
  CA:"M200 70 L340 65 L355 80 L350 110 L320 120 L280 115 L220 118 L195 100 Z",
  US:"M185 130 L340 125 L345 160 L330 180 L280 185 L210 182 L185 165 Z",
  GB:"M476 100 L484 95 L487 100 L484 108 L476 112 L472 106 Z",
  DZ:"M488 155 L520 152 L528 160 L525 185 L510 192 L490 188 L482 175 L485 162 Z",
  MA:"M470 155 L487 152 L489 162 L486 175 L474 178 L466 168 L466 160 Z",
  SN:"M454 205 L467 202 L472 208 L469 218 L460 220 L453 214 Z",
  CI:"M462 225 L476 222 L482 228 L479 238 L468 241 L460 235 Z",
  BJ:"M496 220 L503 218 L507 224 L505 232 L498 234 L493 228 Z",
  TG:"M486 220 L494 218 L497 226 L494 234 L486 235 L483 228 Z",
  CM:"M505 212 L522 208 L530 216 L527 232 L514 238 L504 232 Z",
  NG:"M496 205 L522 202 L530 210 L528 224 L510 228 L495 224 Z",
  GH:"M470 215 L483 212 L487 220 L484 230 L472 232 L467 224 Z",
};

/* Centres des labels texte par pays */
const LABEL_CENTERS = {
  FR:[491,123],CI:[471,232],SN:[462,211],CM:[517,223],MA:[477,165],
  BJ:[499,226],TG:[490,227],BE:[493,111],DZ:[505,170],CH:[500,121],
  CA:[275,95], US:[265,155],GB:[480,104],NG:[513,214],GH:[477,221],
};

/* Fond (pays non tracés) */
const BASE_PATHS = {
  RU:"M550 55 L750 48 L820 62 L840 80 L800 100 L740 105 L680 95 L600 88 L555 75 Z",
  CN:"M700 130 L780 125 L800 145 L790 170 L760 178 L720 175 L690 160 L688 140 Z",
  IN:"M660 160 L700 158 L715 180 L710 210 L690 225 L668 218 L650 195 L648 170 Z",
  AU:"M730 290 L810 285 L840 305 L835 340 L800 355 L755 348 L720 330 L715 305 Z",
  BR:"M270 240 L340 235 L365 250 L358 300 L330 320 L290 318 L260 295 L255 260 Z",
  AR:"M265 318 L310 315 L325 340 L315 380 L290 395 L265 385 L250 360 L252 330 Z",
  MX:"M155 185 L215 180 L230 200 L220 220 L195 228 L165 220 L148 205 Z",
  JP:"M810 140 L830 135 L840 148 L835 165 L820 168 L808 155 Z",
  ZA:"M510 330 L545 325 L560 345 L555 370 L530 378 L508 365 L500 345 Z",
  EG:"M535 155 L560 152 L568 168 L563 185 L540 188 L528 172 Z",
  ID:"M740 255 L840 248 L855 265 L845 280 L790 285 L738 272 Z",
  SA:"M560 170 L605 165 L618 185 L610 210 L585 218 L558 205 L550 185 Z",
  TR:"M530 128 L580 124 L590 136 L582 148 L545 152 L528 140 Z",
  PK:"M620 148 L660 143 L672 158 L665 175 L635 180 L618 165 Z",
  SD:"M536 190 L570 185 L580 200 L575 222 L550 228 L532 215 Z",
  ET:"M548 215 L580 210 L590 225 L583 245 L558 250 L542 237 Z",
  CD:"M510 240 L555 235 L568 252 L560 280 L530 288 L505 275 L498 255 Z",
  KZ:"M595 95 L680 88 L690 108 L680 128 L625 132 L590 115 Z",
  DE:"M500 100 L520 96 L528 108 L522 120 L505 124 L496 112 Z",
  ES:"M460 128 L490 124 L498 136 L490 148 L465 152 L454 140 Z",
  IT:"M505 128 L522 124 L532 136 L525 155 L512 158 L500 145 Z",
  PL:"M510 98 L535 94 L542 106 L535 118 L515 122 L506 110 Z",
  UA:"M525 105 L562 100 L570 112 L560 124 L528 128 L518 116 Z",
  SE:"M502 68 L518 62 L525 78 L520 95 L508 98 L498 85 Z",
  NO:"M484 65 L505 58 L508 72 L502 88 L486 90 L480 75 Z",
  FI:"M520 62 L540 56 L548 70 L542 88 L525 92 L516 78 Z",
};

/* ─── Couleurs de métrique ────────────────────────────────────────────────── */
const METRICS = [
  { id:"sessions",  label:"Sessions",    key:"sessions",  fmtRow:n=>n.toLocaleString("fr-FR"), color:"#00e676" },
  { id:"users",     label:"Utilisateurs",key:"users",     fmtRow:n=>n.toLocaleString("fr-FR"), color:"#4fc3f7" },
  { id:"volume",    label:"Volume EVP",  key:"volume",    fmtRow:n=>n>=1000?`${(n/1000).toFixed(1)}K`:String(n), color:"#ffc107" },
];

function hexToRgb(hex){ return [parseInt(hex.slice(1,3),16),parseInt(hex.slice(3,5),16),parseInt(hex.slice(5,7),16)]; }
function getColor(val, maxV, color) {
  const t = Math.pow(val / (maxV||1), 0.6);
  const [r,g,b] = hexToRgb(color);
  const bg = [14,17,21];
  return `rgb(${Math.round(bg[0]+(r-bg[0])*t)},${Math.round(bg[1]+(g-bg[1])*t)},${Math.round(bg[2]+(b-bg[2])*t)})`;
}

/* ─── Styled components ───────────────────────────────────────────────────── */
const Wrap = styled.div`display:flex;flex-direction:column;gap:0;`;

const MapHead = styled.div`
  display:flex;align-items:flex-start;justify-content:space-between;
  margin-bottom:12px;flex-wrap:wrap;gap:10px;
`;
const MapTitle = styled.div`
  font-family:${({theme})=>theme.fontMono};font-size:.62rem;text-transform:uppercase;
  letter-spacing:.08em;color:${({theme})=>theme.textMuted};margin-bottom:6px;
`;
const StatRow = styled.div`display:flex;gap:16px;flex-wrap:wrap;`;
const StatPill = styled.span`
  font-family:${({theme})=>theme.fontMono};font-size:.68rem;color:${({theme})=>theme.textMuted};
  strong{color:${({theme})=>theme.text};}
`;
const MetricTabs = styled.div`display:flex;gap:4px;`;
const MTab = styled.button`
  font-family:${({theme})=>theme.fontMono};font-size:.6rem;
  padding:.3rem .75rem;border-radius:6px;cursor:pointer;transition:all .15s;white-space:nowrap;
  border:1px solid ${({$a,$c,theme})=>$a?$c+"99":theme.border};
  background:${({$a,$c})=>$a?$c+"15":"transparent"};
  color:${({$a,$c,theme})=>$a?$c:theme.textMuted};
`;

const MapContainer = styled.div`
  width:100%;border-radius:10px;overflow:hidden;
  background:${({theme})=>theme.name==="dark"?"#080a0c":theme.bgHover};
  border:1px solid ${({theme})=>theme.border};
  margin-bottom:12px;position:relative;
`;
const WorldSvg = styled.svg`width:100%;height:auto;display:block;cursor:default;`;

/* Légende gradient */
const LegendRow = styled.div`
  display:flex;align-items:center;gap:8px;
  padding:7px 12px;border-top:1px solid ${({theme})=>theme.border};
`;
const LegendLabel = styled.span`font-family:${({theme})=>theme.fontMono};font-size:.6rem;color:${({theme})=>theme.textMuted};`;
const LegendCanvas = styled.canvas`flex:1;height:8px;border-radius:4px;max-width:180px;`;

/* Tooltip */
const Tooltip = styled.div`
  position:fixed;pointer-events:none;
  background:${({theme})=>theme.bgCard};
  border:1px solid ${({theme})=>theme.borderLight};
  border-radius:8px;padding:10px 13px;
  font-family:${({theme})=>theme.fontMono};font-size:.65rem;
  z-index:1000;min-width:155px;
  box-shadow:0 8px 24px rgba(0,0,0,.45);
  opacity:${({$visible})=>$visible?1:0};
  transition:opacity .1s;
  pointer-events:none;
`;
const TtFlag = styled.div`font-size:18px;margin-bottom:3px;`;
const TtName = styled.div`font-size:.78rem;font-weight:700;color:${({theme})=>theme.text};margin-bottom:5px;font-family:${({theme})=>theme.fontBody};`;
const TtRow2 = styled.div`display:flex;justify-content:space-between;gap:18px;color:${({theme})=>theme.textMuted};line-height:1.6;`;
const TtVal = styled.span`color:${({theme})=>theme.text};font-weight:600;`;
const TtBar = styled.div`height:3px;border-radius:2px;background:${({theme})=>theme.border};margin-top:5px;overflow:hidden;`;
const TtFill = styled.div`height:100%;border-radius:2px;transition:width .2s;`;

/* Table */
const TableWrap = styled.div`overflow-x:auto;`;
const Table = styled.table`width:100%;border-collapse:collapse;font-size:.72rem;`;
const TH = styled.th`
  text-align:${({$r})=>$r?"right":"left"};padding:.5rem .75rem;
  font-family:${({theme})=>theme.fontMono};font-size:.58rem;text-transform:uppercase;
  letter-spacing:.07em;color:${({theme})=>theme.textMuted};
  border-bottom:1px solid ${({theme})=>theme.border};white-space:nowrap;
`;
const TR = styled.tr`
  border-bottom:1px solid ${({theme})=>theme.border};
  cursor:default;transition:background .12s;
  &:last-child{border-bottom:none;}
  &:hover, &.highlighted{background:${({theme})=>theme.bgHover};}
`;
const TD = styled.td`padding:.6rem .75rem;vertical-align:middle;`;
const Rank = styled.span`font-family:${({theme})=>theme.fontMono};font-size:.6rem;color:${({theme})=>theme.textMuted};`;
const FlagName = styled.div`display:flex;align-items:center;gap:8px;`;
const Flag = styled.span`font-size:15px;`;
const CName = styled.span`font-size:.75rem;color:${({theme})=>theme.text};`;
const BarWrap = styled.div`display:flex;align-items:center;gap:6px;`;
const BarTrack = styled.div`flex:1;height:5px;border-radius:3px;background:${({theme})=>theme.border};overflow:hidden;min-width:60px;`;
const BarFill = styled.div`
  height:100%;border-radius:3px;
  background:${({$c})=>$c||"#00e676"};
  width:${({$w})=>$w}%;
  animation:${grow} .6s cubic-bezier(.4,0,.2,1) both;
  transform-origin:left;
`;
const Pct = styled.span`font-family:${({theme})=>theme.fontMono};font-size:.6rem;color:${({theme})=>theme.textMuted};min-width:28px;`;

/* ─── Main Component ──────────────────────────────────────────────────────── */
export default function WorldSessionsMap({ data }) {
  /* data = tableau de {code, name, flag, sessions, users, volume, trades} */
  const [activeMetric, setActiveMetric] = useState(0);
  const [hovered, setHovered] = useState(null); // code pays
  const [ttPos,   setTtPos]   = useState({ x:0, y:0 });
  const lgdRef = useRef(null);
  const rows = data || [];
  const metric = METRICS[activeMetric];
  const sorted = useMemo(()=>[...rows].sort((a,b)=>b[metric.key]-a[metric.key]),[rows,metric]);
  const maxVal = useMemo(()=>Math.max(...rows.map(d=>d[metric.key]),1),[rows,metric]);
  const totalSessions = useMemo(()=>rows.reduce((s,d)=>s+d.sessions,0),[rows]);
  const totalUsers    = useMemo(()=>rows.reduce((s,d)=>s+d.users,0),[rows]);

  /* Légende gradient */
  useEffect(()=>{
    if(!lgdRef.current)return;
    const c=lgdRef.current, ctx=c.getContext('2d');
    const g=ctx.createLinearGradient(0,0,180,0);
    g.addColorStop(0,'#080a0c');g.addColorStop(1,metric.color);
    ctx.fillStyle=g;ctx.fillRect(0,0,180,8);
  },[activeMetric]);

  const hoveredData = hovered ? rows.find(d=>d.code===hovered) : null;

  return (
    <Wrap>
      {/* Header */}
      <MapHead>
        <div>
          <MapTitle>Sessions par pays · Carte mondiale</MapTitle>
          <StatRow>
            <StatPill><strong>{totalSessions.toLocaleString("fr-FR")}</strong> sessions</StatPill>
            <StatPill><strong>{totalUsers.toLocaleString("fr-FR")}</strong> utilisateurs</StatPill>
            <StatPill><strong>{rows.length}</strong> pays</StatPill>
          </StatRow>
        </div>
        <MetricTabs>
          {METRICS.map((m,i)=>(
            <MTab key={m.id} $a={activeMetric===i} $c={m.color} onClick={()=>setActiveMetric(i)}>
              {m.label}
            </MTab>
          ))}
        </MetricTabs>
      </MapHead>

      {/* Map */}
      <MapContainer>
        <WorldSvg viewBox="0 0 1000 500">
          {/* Océan */}
          <rect width="1000" height="500" fill="#0a1520"/>

          {/* Pays de fond (non tracés) */}
          {Object.entries(BASE_PATHS).map(([code,path])=>(
            <path key={code} d={path} fill="#141c28" stroke="#1e2a3a" strokeWidth="0.5"/>
          ))}

          {/* Pays tracés — choroplèthe */}
          {rows.map(d=>{
            if(!COUNTRY_PATHS[d.code]) return null;
            const fill = getColor(d[metric.key], maxVal, metric.color);
            const isHov = hovered===d.code;
            return (
              <path
                key={d.code}
                d={COUNTRY_PATHS[d.code]}
                fill={fill}
                stroke={isHov ? metric.color : "#0a0d12"}
                strokeWidth={isHov ? "1.5" : "0.8"}
                style={{
                  cursor:"pointer",
                  opacity: hovered && !isHov ? 0.45 : 1,
                  filter: isHov ? "brightness(1.3)" : "none",
                  transition:"opacity .15s, filter .15s",
                }}
                onMouseEnter={e=>{ setHovered(d.code); setTtPos({x:e.clientX,y:e.clientY}); }}
                onMouseMove={e=>setTtPos({x:e.clientX,y:e.clientY})}
                onMouseLeave={()=>setHovered(null)}
              />
            );
          })}

          {/* Labels codes pays */}
          {rows.map(d=>{
            const c = LABEL_CENTERS[d.code];
            if(!c) return null;
            return (
              <text key={d.code} x={c[0]} y={c[1]+4} textAnchor="middle"
                fontSize="8" fill="rgba(255,255,255,0.4)"
                fontFamily="DM Mono,monospace" style={{pointerEvents:"none"}}>
                {d.code}
              </text>
            );
          })}
        </WorldSvg>

        {/* Légende */}
        <LegendRow>
          <LegendLabel>0</LegendLabel>
          <LegendCanvas ref={lgdRef} width={180} height={8}/>
          <LegendLabel>{metric.fmtRow(maxVal)}</LegendLabel>
          <LegendLabel style={{marginLeft:"auto"}}>{metric.label}</LegendLabel>
        </LegendRow>
      </MapContainer>

      {/* Tooltip */}
      {hoveredData && (
        <Tooltip $visible style={{left:ttPos.x+14,top:ttPos.y-10}}>
          <TtFlag>{hoveredData.flag}</TtFlag>
          <TtName>{hoveredData.name}</TtName>
          <TtRow2><span>Sessions</span><TtVal>{hoveredData.sessions.toLocaleString("fr-FR")}</TtVal></TtRow2>
          <TtRow2><span>Utilisateurs</span><TtVal>{hoveredData.users.toLocaleString("fr-FR")}</TtVal></TtRow2>
          <TtRow2><span>Volume</span><TtVal>{metric.fmtRow(hoveredData[metric.key])} EVP</TtVal></TtRow2>
          <TtBar><TtFill $c={metric.color} style={{width:`${Math.round(hoveredData.sessions/totalSessions*100)}%`,background:metric.color}}/></TtBar>
        </Tooltip>
      )}

      {/* Tableau */}
      <TableWrap>
        <Table>
          <thead>
            <tr>
              <TH>#</TH>
              <TH>Pays</TH>
              <TH $r>Sessions</TH>
              <TH $r>Utilisateurs</TH>
              <TH $r>Volume EVP</TH>
              <TH style={{paddingLeft:"12px"}}>Part</TH>
            </tr>
          </thead>
          <tbody>
            {sorted.map((d,i)=>{
              const pct = totalSessions > 0 ? Math.round((d.sessions/totalSessions)*100) : 0;
              const barW = maxVal > 0 ? Math.round((d[metric.key]/maxVal)*100) : 0;
              const medal = i===0?"🥇":i===1?"🥈":i===2?"🥉":null;
              return (
                <TR key={d.code}
                  className={hovered===d.code?"highlighted":""}
                  onMouseEnter={()=>setHovered(d.code)}
                  onMouseLeave={()=>setHovered(null)}>
                  <TD><Rank>{medal||(i<9?"#0"+(i+1):"#"+(i+1))}</Rank></TD>
                  <TD><FlagName><Flag>{d.flag}</Flag><CName>{d.name}</CName></FlagName></TD>
                  <TD style={{textAlign:"right",fontFamily:"monospace",color:"#00e676"}}>{d.sessions.toLocaleString("fr-FR")}</TD>
                  <TD style={{textAlign:"right",fontFamily:"monospace",color:"#4fc3f7"}}>{d.users.toLocaleString("fr-FR")}</TD>
                  <TD style={{textAlign:"right",fontFamily:"monospace",color:"#ffc107"}}>{metric.fmtRow(d.volume)}</TD>
                  <TD style={{paddingLeft:"12px"}}>
                    <BarWrap>
                      <BarTrack><BarFill $c={metric.color} $w={barW}/></BarTrack>
                      <Pct>{pct}%</Pct>
                    </BarWrap>
                  </TD>
                </TR>
              );
            })}
          </tbody>
        </Table>
      </TableWrap>
    </Wrap>
  );
}
